import { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import Box from "@mui/material/Box";
import Accordion from "@mui/material/Accordion";
import { StyledAccordionSummary } from "../../../styles/componentStyled";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Grid from "@mui/material/Grid";
import {
  Button,
  Divider,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Block,
  CheckCircle,
  LockReset,
  Save,
} from "@mui/icons-material";

import Toast from "../../../components/Layout/Toast";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import {
  adminResetPassword,
  fetchRoles,
  updateUser,
} from "../setupSlices";
import { selectSetups } from "../setupSlices";
import { UserRoles } from "./components/UserRoles";
import { Role, UserStatus } from "../../auth/types/authType";
import { selectAuth } from "../../auth/authSlice";
import { AuthState } from "../../auth/types/authType";
import { CompanyFilter } from "../../../components/filter/CompanyFilter";
import { Company } from "../types/setupTypes";

interface UserProps {
  userId: string;
}
export const User = ({ userId }: UserProps) => {
  const { selectedUser, success, error } = useAppSelector(selectSetups);
  const { user } = useAppSelector(selectAuth) as AuthState;

  const dispatch = useAppDispatch();

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState<Company>();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  useEffect(() => {
    dispatch(fetchRoles("all"));
  }, [dispatch, userId]);

  // Re-seed the editable fields whenever a different user is loaded.
  useEffect(() => {
    setName(selectedUser?.name ?? "");
    setEmail(selectedUser?.email ?? "");
    setPassword("");
    setConfirmPassword("");
  }, [selectedUser?._id, selectedUser?.name, selectedUser?.email]);

  const isDisabled = selectedUser?.status === UserStatus.Disabled;

  const saveProfile = () => {
    if (!selectedUser?._id) return;
    dispatch(
      updateUser({
        _id: selectedUser._id,
        name: name.trim(),
        email: email.trim(),
        companyId: company?._id,
      }),
    );
  };

  const toggleStatus = () => {
    if (!selectedUser?._id) return;
    dispatch(
      updateUser({
        _id: selectedUser._id,
        status: isDisabled ? UserStatus.Active : UserStatus.Disabled,
      }),
    );
  };

  const resetPassword = () => {
    if (!selectedUser?._id) return;
    dispatch(
      adminResetPassword({
        userId: selectedUser._id,
        password,
        confirmPassword,
      }),
    );
    setPassword("");
    setConfirmPassword("");
  };

  const passwordValid =
    password.length >= 6 && password === confirmPassword;

  return (
    <>
      <Helmet>
        <title> {`${selectedUser?.name} Account`}</title>
      </Helmet>
      <Box
        sx={{
          m: { xs: 0, md: 1 },
          px: { xs: 0, md: 2 },
        }}
      >
        {/* Name + Email (editable) */}
        <Grid container spacing={3}>
          <Grid item md={6} xs={12}>
            <TextField
              id="nameId"
              label="Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              variant="outlined"
              fullWidth
              sx={{ mt: 1 }}
            />
          </Grid>
          <Grid item md={6} xs={12}>
            <TextField
              id="emailId"
              label="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              variant="outlined"
              fullWidth
              sx={{ mt: 1 }}
            />
          </Grid>
        </Grid>

        {/* Company change */}
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" color="textSecondary">
            Company
          </Typography>
          <CompanyFilter
            setCompany={setCompany}
            companyId={selectedUser?.company?._id}
            includeAll={false}
            includeModerator={true}
          />
        </Box>

        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          sx={{ mt: 2, flexWrap: "wrap" }}
        >
          <Button
            color="secondary"
            variant="contained"
            startIcon={<Save />}
            onClick={saveProfile}
            disabled={name.trim().length === 0 || email.trim().length === 0}
          >
            Save Profile
          </Button>

          <Tooltip
            title={
              isDisabled
                ? "Re-activate this user"
                : "Disable this user's access"
            }
          >
            <Button
              color={isDisabled ? "success" : "error"}
              variant="outlined"
              startIcon={isDisabled ? <CheckCircle /> : <Block />}
              onClick={toggleStatus}
            >
              {isDisabled ? "Activate User" : "Disable User"}
            </Button>
          </Tooltip>

          <Typography variant="body2" color="textSecondary">
            Status: {selectedUser?.status ?? UserStatus.Waiting}
          </Typography>
        </Stack>

        <Divider sx={{ my: 2 }} />

        {/* Password reset (admin sets a new password) */}
        <Accordion sx={{ my: 1 }}>
          <StyledAccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="reset-password-content"
            id="reset-password-header"
          >
            <Typography variant="h5" component="div">
              <LockReset sx={{ verticalAlign: "middle", mr: 1 }} />
              Password Reset
            </Typography>
          </StyledAccordionSummary>
          <AccordionDetails>
            <Grid container spacing={3}>
              <Grid item md={6} xs={12}>
                <TextField
                  id="passwordId"
                  label="New Password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  variant="outlined"
                  fullWidth
                  helperText="At least 6 characters"
                />
              </Grid>
              <Grid item md={6} xs={12}>
                <TextField
                  id="confirmPasswordId"
                  label="Confirm Password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  variant="outlined"
                  fullWidth
                  error={
                    confirmPassword.length > 0 && password !== confirmPassword
                  }
                  helperText={
                    confirmPassword.length > 0 && password !== confirmPassword
                      ? "Passwords do not match"
                      : " "
                  }
                />
              </Grid>
            </Grid>
            <Button
              sx={{ mt: 1 }}
              color="secondary"
              variant="contained"
              startIcon={<LockReset />}
              onClick={resetPassword}
              disabled={!passwordValid}
            >
              Reset Password
            </Button>
          </AccordionDetails>
        </Accordion>

        {/* Roles */}
        <Accordion sx={{ my: 1 }}>
          <StyledAccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="panel1a-content"
            id="panel1a-header"
          >
            <Typography variant="h5" component="div">
              Roles
            </Typography>
          </StyledAccordionSummary>
          <AccordionDetails>
            <UserRoles userId={userId} allRoles={user?.roles as Role[]} />
          </AccordionDetails>
        </Accordion>

        {success && <Toast severity="success">{success.message}</Toast>}
        {error && <Toast severity="error">{error.message}</Toast>}
      </Box>
    </>
  );
};
